import { Dropdown } from "Common/Components/Dropdown";
import { useAppServices } from "hook/services";
import {
  CheckCircle,
  Eye,
  FileEdit,
  MoreHorizontal,
  Trash2,
  X,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import SubAccountEditModal from "../Modal/Edit";
import { useUserInfo } from "context/user";

export default function SubAccountsData() {
  const AppService = useAppServices();
  const { user } = useUserInfo();
  const [subAccounts, setSubAccounts] = useState<any>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [editData, setEditData] = useState<any>(null);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);

  // Server-side pagination and filtering states
  const [pagination, setPagination] = useState({
    page: 1, // Backend uses 1-based page indexing
    limit: 10,
    total: 0,
    pages: 0,
  });
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [sorting, setSorting] = useState<{ field: string; direction: string }>({
    field: "createdAt",
    direction: "desc",
  });

  const toggleEditModal = () => setShowEditModal((prev) => !prev);

  const buildQueryString = () => {
    const params = new URLSearchParams();

    // Required company filter
    if (user?.companyId) {
      params.append("companyId", user.companyId);
    }

    // Pagination parameters (matching backend)
    params.append("page", pagination.page.toString());
    params.append("limit", pagination.limit.toString());

    // Search parameter
    if (searchTerm) {
      params.append("search", searchTerm);
    }

    // Sorting parameters
    if (sorting.field && sorting.direction) {
      params.append("sortBy", sorting.field);
      params.append("sortOrder", sorting.direction);
    }

    return params.toString();
  };

  const getSubAccounts = async () => {
    setLoading(true);
    try {
      const query = buildQueryString();
      const { response, error } = await AppService.sub_account.filter({
        query: query,
      });

      if (response && response.data) {
        setSubAccounts(response.data);

        // Update pagination info from API response
        if (response.pagination) {
          setPagination({
            page: response.pagination.page,
            limit: response.pagination.limit,
            total: response.pagination.total,
            pages: response.pagination.pages,
          });
        }
      } else {
        setSubAccounts([]);
        setPagination((prev) => ({
          ...prev,
          total: 0,
          pages: 0,
        }));
      }
    } catch (error) {
      console.error("Error fetching sub accounts:", error);
      setSubAccounts([]);
    } finally {
      setLoading(false);
    }
  };
  let pageLoad = true;
  // Load data when dependencies change
  useEffect(() => {
    if (pageLoad) {
      getSubAccounts();
      pageLoad = false;
    }
  }, [pagination.page, pagination.limit, searchTerm, sorting]);

  const Status = ({ item }: any) => {
    switch (item) {
      case "active":
        return (
          <span className="px-2.5 py-0.5 text-xs font-medium rounded border bg-green-100 border-transparent text-green-500 dark:bg-green-500/20 dark:border-transparent inline-flex items-center status">
            <CheckCircle className="size-3 mr-1.5" />
            {item}
          </span>
        );
      case "inactive":
        return (
          <span className="px-2.5 py-0.5 inline-flex items-center text-xs font-medium rounded border bg-red-100 border-transparent text-red-500 dark:bg-red-500/20 dark:border-transparent status">
            <X className="size-3 mr-1.5" />
            {item}
          </span>
        );
      default:
        return (
          <span className="px-2.5 py-0.5 text-xs font-medium rounded border bg-green-100 border-transparent text-green-500 dark:bg-green-500/20 dark:border-transparent inline-flex items-center status">
            <CheckCircle className="size-3 mr-1.5" />
            {item}
          </span>
        );
    }
  };

  const toggleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedRows(subAccounts.map((acc: any) => acc._id));
    } else {
      setSelectedRows([]);
    }
  };

  const toggleSelectRow = (id: string, checked: boolean) => {
    if (checked) {
      setSelectedRows((prev) => [...prev, id]);
    } else {
      setSelectedRows((prev) => prev.filter((rowId) => rowId !== id));
    }
  };

  const handleDelete = async (_id: string) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const { response, error } = await AppService.sub_account.delete({
          query: `_id=${_id}`,
        });

        if (response) {
          Swal.fire("Deleted!", "The record has been deleted.", "success");
          getSubAccounts(); // Refresh data
        } else {
          Swal.fire(
            "Error!",
            error?.message || "Something went wrong.",
            "error"
          );
        }
      } catch (err: any) {
        Swal.fire("Error!", err.message || "Delete failed.", "error");
      }
    }
  };

  // Handle search
  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setPagination((prev) => ({ ...prev, page: 1 })); // Reset to first page on search
  };

  // Handle page change (1-based)
  const handlePageChange = (page: number) => {
    setPagination((prev) => ({ ...prev, page }));
  };

  // Handle page size change
  const handlePageSizeChange = (limit: number) => {
    setPagination((prev) => ({ ...prev, limit, page: 1 }));
  };

  // Handle sorting
  const handleSort = (field: string, direction: string) => {
    setSorting({ field, direction });
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const columns = useMemo(
    () => [
      {
        header: (() => {
          const allSelected =
            selectedRows.length === subAccounts.length &&
            subAccounts.length > 0;

          if (allSelected) {
            return (
              <input
                id="CheckboxAll"
                className="size-4 cursor-pointer"
                type="checkbox"
                checked
                onChange={(e) => toggleSelectAll(e.target.checked)}
              />
            );
          }
          return (
            <input
              id="CheckboxAll"
              className="size-4 cursor-pointer"
              type="checkbox"
              onChange={(e) => toggleSelectAll(e.target.checked)}
            />
          );
        })(),
        id: "checkAll",
        cell: (cell: any) => {
          const rowId = cell.row.original._id;
          if (selectedRows.includes(rowId))
            return (
              <input
                className="size-4 cursor-pointer"
                type="checkbox"
                checked
                onChange={(e) => toggleSelectRow(rowId, e.target.checked)}
              />
            );
          return (
            <input
              className="size-4 cursor-pointer"
              type="checkbox"
              onChange={(e) => toggleSelectRow(rowId, e.target.checked)}
            />
          );
        },
      },
      {
        header: "Name",
        accessorKey: "name",
        enableSorting: true,
        cell: (cell: any) => (
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center size-10 font-medium rounded-full shrink-0 bg-slate-200 text-slate-800 dark:text-zink-50 dark:bg-zink-600">
              {cell.row.original.img ? (
                <img
                  src={cell.row.original.img}
                  alt=""
                  className="h-10 rounded-full"
                />
              ) : (
                cell
                  .getValue()
                  .split(" ")
                  .map((word: any) => word.charAt(0))
                  .join("")
              )}
            </div>
            <div className="grow">
              <h6 className="mb-1">
                <Link to="#!" className="name">
                  {cell.getValue()}
                </Link>
              </h6>
              <p className="text-slate-500 dark:text-zink-200">
                {cell.row.original.designation}
              </p>
            </div>
          </div>
        ),
      },
      {
        header: "Email",
        accessorKey: "email",
        enableColumnFilter: false,
      },
      {
        header: "Phone Number",
        accessorKey: "phone",
        enableColumnFilter: false,
      },
      {
        header: "Status",
        accessorKey: "status",
        enableColumnFilter: false,
        cell: (cell: any) => <Status item={cell.getValue()} />,
      },
      {
        header: "Action",
        enableColumnFilter: false,
        cell: (cell: any) => (
          <Dropdown className="relative">
            <Dropdown.Trigger
              className="flex items-center justify-center size-[30px] p-0 text-slate-500 btn bg-slate-100 hover:text-white hover:bg-slate-600 focus:text-white focus:bg-slate-600 focus:ring focus:ring-slate-100 active:text-white active:bg-slate-600 active:ring active:ring-slate-100 dark:bg-slate-500/20 dark:text-slate-400 dark:hover:bg-slate-500 dark:hover:text-white dark:focus:bg-slate-500 dark:focus:text-white dark:active:bg-slate-500 dark:active:text-white dark:ring-slate-400/20"
              id="usersAction1"
            >
              <MoreHorizontal className="size-3" />
            </Dropdown.Trigger>
            <Dropdown.Content
              placement="right-end"
              className="absolute z-50 py-2 mt-1 ltr:text-left rtl:text-right list-none bg-white rounded-md shadow-md min-w-[10rem] dark:bg-zink-600"
              aria-labelledby="usersAction1"
            >
              <li>
                <div className="block px-4 py-1.5 text-base transition-all duration-200 ease-linear text-slate-600 hover:bg-slate-100 hover:text-slate-500 focus:bg-slate-100 focus:text-slate-500 dark:text-zink-100 dark:hover:bg-zink-500 dark:hover:text-zink-200 dark:focus:bg-zink-500 dark:focus:text-zink-200 cursor-pointer">
                  <Eye className="inline-block size-3 ltr:mr-1 rtl:ml-1" />{" "}
                  <span className="align-middle">Overview</span>
                </div>
              </li>
              <li>
                <div
                  onClick={() => {
                    toggleEditModal();
                    setEditData(cell.row.original);
                  }}
                  data-modal-target="addUserModal"
                  className="block px-4 py-1.5 text-base transition-all duration-200 ease-linear text-slate-600 hover:bg-slate-100 hover:text-slate-500 focus:bg-slate-100 focus:text-slate-500 dark:text-zink-100 dark:hover:bg-zink-500 dark:hover:text-zink-200 dark:focus:bg-zink-500 dark:focus:text-zink-200 cursor-pointer"
                >
                  <FileEdit className="inline-block size-3 ltr:mr-1 rtl:ml-1" />{" "}
                  <span className="align-middle">Edit</span>
                </div>
              </li>
              <li>
                <div
                  className="block px-4 py-1.5 text-base transition-all duration-200 ease-linear text-slate-600 hover:bg-slate-100 hover:text-slate-500 focus:bg-slate-100 focus:text-slate-500 dark:text-zink-100 dark:hover:bg-zink-500 dark:hover:text-zink-200 dark:focus:bg-zink-500 dark:focus:text-zink-200 cursor-pointer"
                  onClick={() => handleDelete(cell.row.original._id)}
                >
                  <Trash2 className="inline-block size-3 ltr:mr-1 rtl:ml-1" />{" "}
                  <span className="align-middle">Delete</span>
                </div>
              </li>
            </Dropdown.Content>
          </Dropdown>
        ),
      },
    ],
    [selectedRows, subAccounts]
  );

  return {
    loading,
    subAccounts,
    selectedRows,
    pagination,
    searchTerm,
    resetSelectedRows: () => setSelectedRows([]),
    columns,
    handleRefresh: getSubAccounts,
    handleSearch,
    handlePageChange,
    handlePageSizeChange,
    handleSort,
    sorting,
    EditModel: (
      <SubAccountEditModal
        handleRefresh={getSubAccounts}
        show={showEditModal}
        toggle={toggleEditModal}
        editData={editData}
      />
    ),
  };
}
